import { Paper, Text } from '@mantine/core';
import { Redirect, useParams } from 'wouter';
import { mondayLinkPath, useResolveMonday } from '@/models/clientCompanies';
import { type PersonCardResponse, usePersonCard } from '@/models/persons';
import { PersonView } from '@/pages/persons/PersonPage';
import MondayContactKycPanel from './MondayContactKycPanel';

const MONDAY_PULSE_THRESHOLD = 1_000_000;

export default function ContactDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  if (id > MONDAY_PULSE_THRESHOLD) {
    return <MondayContact pulseId={id} />;
  }

  return <LocalPerson id={id} />;
}

function MondayContact({ pulseId }: { pulseId: number }) {
  const person = usePersonCard({ pulse: pulseId });
  if (person.isLoading) return null;
  const card = person.data as PersonCardResponse | undefined;
  if (card?.person) {
    const to = card.selectedLeadId
      ? `/leads/${card.selectedLeadId}`
      : `/contacts/${card.person.id}`;
    return <Redirect to={`~${to}`} replace />;
  }
  return <MondayFallback pulseId={pulseId} />;
}

function MondayFallback({ pulseId }: { pulseId: number }) {
  const { data, isLoading } = useResolveMonday(pulseId);
  if (isLoading) return null;
  const path = data && data.kind !== 'deal' ? mondayLinkPath(data) : null;
  if (path) return <Redirect to={`~${path}`} replace />;
  return <MondayContactKycPanel pulseId={String(pulseId)} />;
}

function LocalPerson({ id }: { id: number }) {
  const { data, isLoading } = usePersonCard({ id });
  if (isLoading) return null;
  const card = data as PersonCardResponse | undefined;
  if (!card?.person) {
    return (
      <Paper p="md" h="100%">
        <Text c="dimmed">
          Contact not found, or you no longer see it: it is assigned to other
          users.
        </Text>
      </Paper>
    );
  }
  return <PersonView key={card.person.id} data={card} />;
}
